import { matchPath } from 'react-router-dom';

/**
 * LE NUMÉRO DE CHAQUE PAGE DU SITE.
 *
 * Demande d'Emeni (2026-08-28) : « j'aimerais que tu numérotes toutes les pages de mon site, pour qu'à
 * chaque fois que je te demande de modifier quelque chose je n'aie plus qu'à te donner le numéro ».
 *
 * Le but est d'économiser du temps et des jetons : « la page 46 » remplace une description à retrouver, et
 * m'évite de fouiller le projet pour deviner de quel écran on parle.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * DEUX RÈGLES À NE JAMAIS ENFREINDRE :
 *
 * 1. UN NUMÉRO EST DÉFINITIF. Ne jamais renuméroter, ne jamais réutiliser le numéro d'une page supprimée —
 *    sinon « la page 46 » ne désigne plus la même chose d'une conversation à l'autre, et la référence perd
 *    tout son intérêt. Une page qui disparaît laisse son numéro vacant. Une page nouvelle prend le numéro
 *    suivant, à la fin, quel que soit son rangement thématique.
 * 2. LES NUMÉROS NE S'AFFICHENT QU'EN PREVIEW (voir estEnPreview ci-dessous). Un client ne doit jamais
 *    voir ces pastilles.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 */

export interface PageNumerotee {
    /** Définitif. Voir la règle 1 ci-dessus. */
    numero: number;
    /** Le motif de route, tel qu'il est déclaré dans App.tsx. */
    chemin: string;
    libelle: string;
}

export const PAGES: PageNumerotee[] = [
    // ── Public (hors connexion) ───────────────────────────────────────────────────────────────────
    { numero: 1, chemin: '/', libelle: "Accueil public / Tableau de bord" },
    { numero: 2, chemin: '/login', libelle: 'Connexion' },
    { numero: 3, chemin: '/inscription/:token', libelle: "Inscription d'un client" },
    { numero: 4, chemin: '/reserver/:clientId', libelle: 'Widget de réservation' },
    { numero: 5, chemin: '/rdv-pro-reserver/:clientId', libelle: 'Widget de prise de RDV Pro' },
    { numero: 6, chemin: '/pointage-kiosque/:clientId', libelle: 'Kiosque de pointage' },
    { numero: 7, chemin: '/documentation/:token', libelle: 'Démarche client (réponse)' },
    { numero: 8, chemin: '/mon-repas/:token', libelle: 'Choix du repas par le convive' },
    { numero: 9, chemin: '/ma-table/:publicToken/:tableNumber', libelle: 'Arrivée à table (QR code)' },
    { numero: 10, chemin: '/commande-fournisseur/:token', libelle: 'Confirmation fournisseur' },
    { numero: 11, chemin: '/annulation-rdv/:token', libelle: "Annulation d'un rendez-vous" },
    { numero: 12, chemin: '/extras-demande/:token', libelle: 'Confirmation partenaire Extras' },
    { numero: 13, chemin: '/devis/:token', libelle: 'Signature du devis par le client' },
    { numero: 14, chemin: '/legal', libelle: 'Mentions légales' },
    { numero: 15, chemin: '/privacy', libelle: 'Politique de confidentialité' },

    // ── Portail salarié ───────────────────────────────────────────────────────────────────────────
    { numero: 16, chemin: '/mon-tableau-de-bord', libelle: 'Salarié — tableau de bord' },
    { numero: 17, chemin: '/feuille-de-route', libelle: 'Salarié — feuille de route' },
    { numero: 18, chemin: '/absences', libelle: 'Salarié — mes absences' },
    { numero: 19, chemin: '/mes-sondages', libelle: 'Salarié — sondages' },
    { numero: 20, chemin: '/mon-integration', libelle: 'Salarié — intégration' },
    { numero: 21, chemin: '/mes-notes-de-frais', libelle: 'Salarié — notes de frais' },
    { numero: 22, chemin: '/pointage', libelle: 'Salarié — pointage' },
    { numero: 23, chemin: '/mes-messages', libelle: 'Salarié — messages' },

    // ── Planning ──────────────────────────────────────────────────────────────────────────────────
    { numero: 24, chemin: '/planning', libelle: 'Planning hebdomadaire' },
    { numero: 25, chemin: '/exception-service', libelle: "Service d'exception" },
    { numero: 26, chemin: '/planning-rdv', libelle: 'Planning rendez-vous (Prestation)' },
    { numero: 27, chemin: '/prestations', libelle: 'Prestations' },

    // ── Commercial ────────────────────────────────────────────────────────────────────────────────
    { numero: 28, chemin: '/evenementiel', libelle: 'Événementiel — calendrier' },
    { numero: 29, chemin: '/suivi-privatisation', libelle: 'Suivi privatisation — liste' },
    { numero: 30, chemin: '/suivi-privatisation/:dossierId/pipeline', libelle: 'Dossier — Pipeline' },
    { numero: 31, chemin: '/suivi-privatisation/:dossierId/brief', libelle: 'Dossier — Brief' },
    { numero: 32, chemin: '/suivi-privatisation/:dossierId/documents', libelle: 'Dossier — Documents' },
    { numero: 33, chemin: '/suivi-privatisation/:dossierId/menus', libelle: 'Dossier — Menus & Jour J' },
    { numero: 34, chemin: '/suivi-privatisation/:dossierId/journal', libelle: 'Dossier — Historique' },
    { numero: 35, chemin: '/menus-convives', libelle: 'Menus & Convives — liste' },
    { numero: 36, chemin: '/menus-convives/:eventId', libelle: 'Menus & Convives — suivi' },
    { numero: 37, chemin: '/menus-convives/:eventId/menu', libelle: 'Menus & Convives — choix des plats' },
    { numero: 38, chemin: '/menus-convives/:eventId/qr-codes', libelle: 'QR codes des tables' },

    // ── Cuisine ───────────────────────────────────────────────────────────────────────────────────
    { numero: 39, chemin: '/mode-terrain', libelle: 'Mode terrain — liste des services' },
    { numero: 40, chemin: '/mode-terrain/:eventId', libelle: 'Mode terrain — service en cours' },
    { numero: 41, chemin: '/menu-plats', libelle: 'Menu & Plats — catalogue' },
    { numero: 42, chemin: '/menu-plats/allergenes', libelle: "Familles d'allergènes" },

    // ── Relation client ───────────────────────────────────────────────────────────────────────────
    { numero: 43, chemin: '/crm', libelle: 'CRM — contacts' },
    { numero: 44, chemin: '/crm/contacts/:id', libelle: 'CRM — fiche contact' },
    { numero: 45, chemin: '/crm/contacts/:id/demarches', libelle: 'Contact — Démarches' },
    { numero: 46, chemin: '/crm/contacts/:id/factures', libelle: 'Contact — Factures' },
    { numero: 47, chemin: '/crm/contacts/:id/commandes', libelle: 'Contact — Commandes' },
    { numero: 48, chemin: '/crm/contacts/:id/rendez-vous', libelle: 'Contact — Rendez-vous' },
    { numero: 49, chemin: '/crm/contacts/:id/evenements', libelle: 'Contact — Événements' },
    { numero: 50, chemin: '/rdv-pro', libelle: 'RDV Pro' },
    { numero: 51, chemin: '/rdv-pro/annulations', libelle: 'RDV Pro — annulations' },
    { numero: 52, chemin: '/demandes-recurrentes', libelle: 'Demandes récurrentes' },

    // ── Fournisseurs & Partenaires ────────────────────────────────────────────────────────────────
    { numero: 53, chemin: '/fournisseurs', libelle: 'Fournisseurs — liste' },
    { numero: 54, chemin: '/fournisseurs/:id', libelle: 'Fournisseur — fiche' },
    { numero: 55, chemin: '/fournisseurs/:id/commandes', libelle: 'Fournisseur — commandes' },
    { numero: 56, chemin: '/fournisseurs/:id/articles', libelle: 'Fournisseur — articles' },
    { numero: 57, chemin: '/fournisseurs/:id/articles/:categoryId', libelle: 'Fournisseur — articles par catégorie' },
    { numero: 58, chemin: '/commandes', libelle: 'Commandes' },
    { numero: 59, chemin: '/extras-partenaires', libelle: 'Partenaires Extras — liste' },
    { numero: 60, chemin: '/extras-partenaires/:id', libelle: 'Partenaire Extras — fiche' },
    { numero: 61, chemin: '/extras-partenaires/:id/types', libelle: 'Partenaire — types' },
    { numero: 62, chemin: '/extras-partenaires/:id/types/:categoryId', libelle: 'Partenaire — types par catégorie' },
    { numero: 63, chemin: '/extras-partenaires/:id/demandes', libelle: 'Partenaire — demandes' },
    { numero: 64, chemin: '/extras', libelle: 'Extras — suivi' },

    // ── Facturation, RH, personnel ────────────────────────────────────────────────────────────────
    { numero: 65, chemin: '/factures', libelle: 'Factures' },
    { numero: 66, chemin: '/employes', libelle: 'Postes & Employés' },
    { numero: 67, chemin: '/employes/:id', libelle: 'Fiche employé' },
    { numero: 68, chemin: '/demandes-conges', libelle: 'Demandes de congés' },
    { numero: 69, chemin: '/messagerie', libelle: 'Messagerie' },
    { numero: 70, chemin: '/notes-de-frais', libelle: 'Notes de frais' },
    { numero: 71, chemin: '/pointage-admin', libelle: 'Pointage (administration)' },
    { numero: 72, chemin: '/todo', libelle: 'ToDo' },
    { numero: 73, chemin: '/notes', libelle: 'Notes' },
    { numero: 74, chemin: '/parametres', libelle: 'Paramètres' },

    // ── Administration ────────────────────────────────────────────────────────────────────────────
    { numero: 75, chemin: '/admin/sentinel', libelle: 'Admin — Sentinel' },
    { numero: 76, chemin: '/admin/settings', libelle: 'Admin — réglages' },
    { numero: 77, chemin: '/admin/collaborator-requests', libelle: 'Admin — demandes collaborateur' },
    { numero: 78, chemin: '/admin/exception-service-automation-requests', libelle: "Admin — automatisations service d'exception" },
    { numero: 79, chemin: '/admin/clients', libelle: 'Admin — clients' },
    { numero: 80, chemin: '/admin/clients/:id', libelle: 'Admin — fiche client' },
    { numero: 81, chemin: '/admin/registration-requests', libelle: "Admin — demandes d'inscription" },
    { numero: 82, chemin: '/admin/support', libelle: 'Admin — support' },
];

/** Le prochain numéro libre — à utiliser en ajoutant une page, sans jamais recycler un numéro retiré. */
export const PROCHAIN_NUMERO = PAGES.reduce((max, p) => Math.max(max, p.numero), 0) + 1;

/**
 * Les pastilles n'apparaissent QUE hors production.
 *
 * La production, ce sont exactement deux domaines : gestion.l-iamani.com (celui des clients) et
 * iamani.pages.dev (l'alias de production Cloudflare). Tout le reste — les previews de branche en
 * <alias>.iamani.pages.dev, les déploiements par empreinte, et localhost — affiche les numéros.
 *
 * La liste est volontairement écrite en positif (« voici la production ») plutôt qu'en négatif : une
 * nouvelle preview apparaîtra toute seule, alors qu'un nouveau domaine de production devra être ajouté
 * ici — c'est le sens de l'erreur qu'on préfère, un numéro visible en preview de trop valant mieux qu'un
 * numéro affiché chez un client.
 */
const DOMAINES_DE_PRODUCTION = ['gestion.l-iamani.com', 'iamani.pages.dev'];

export const estEnPreview = (): boolean => {
    if (typeof window === 'undefined') return false;
    return !DOMAINES_DE_PRODUCTION.includes(window.location.hostname);
};

/** Le numéro de la page correspondant à une adresse, ou null si elle n'est pas répertoriée. */
export const trouverNumeroDePage = (pathname: string): PageNumerotee | null => {
    // Le motif le plus précis d'abord : sans ça, « / » capturerait tout.
    const candidats = [...PAGES].sort((a, b) => b.chemin.length - a.chemin.length);
    for (const page of candidats) {
        if (matchPath({ path: page.chemin, end: true }, pathname)) return page;
    }
    return null;
};
